import { TweenMax, Quint } from 'gsap'

import { validateName, validateEmail } from '../../helpers/form-validation'

/**
 * All classes used in the component.
 * @enum {Object<string>}
 */
const CLASSES = {
  form: 'dr-send-your-resume__form',
  formContent: 'dr-send-your-resume__form-content',
  successMessage: 'dr-send-your-resume__success-message',
  error: 'dr-send-your-resume__input-error',
  nameInput: 'dr-send-your-resume__name-input',
  emailInput: 'dr-send-your-resume__email-input',
  fileInput: 'dr-file-input__input',
  fileInputText: 'dr-file-input__text',
  stepCount: 'dr-send-your-resume__step-count',
  backButton: 'dr-send-your-resume__go-back',
  backButtonVisible: 'dr-send-your-resume__go-back--visible',
  inputHolder: 'dr-send-your-resume__input-holder',
  submitButton: 'dr-send-your-resume__submit',
  loader: 'dr-loader'
}

/**
 * All numbers used in the component.
 * @enum {Object<number>}
 */
const NUMBERS = {
  name: 0,
  email: 1,
  attachment: 2,
  success: 3,
  transitionTime: 0.6,
  inputBorder: 3,
  successStatus: 200,
  xhrSuccessState: 4
}

/**
 * @module SendYourResume
 */
class SendYourResume {
  /**
   * @param {HTMLElement} element - The HTMLElement this module is constructed upon
   */
  constructor(element) {
    this.element_ = element
    this.form_ = this.element_.querySelector(`.${CLASSES.form}`)
    this.formContent_ = this.element_.querySelector(`.${CLASSES.formContent}`)
    this.successMessage_ = this.element_.querySelector(
      `.${CLASSES.successMessage}`
    )
    this.backButton_ = this.element_.querySelector(`.${CLASSES.backButton}`)
    this.errorField_ = this.element_.querySelector(`.${CLASSES.error}`)
    this.nameInput_ = this.element_.querySelector(`.${CLASSES.nameInput}`)
    this.emailInput_ = this.element_.querySelector(`.${CLASSES.emailInput}`)
    this.fileInput_ = this.element_.querySelector(`.${CLASSES.fileInput}`)
    this.fileInputText_ = this.element_.querySelector(
      `.${CLASSES.fileInputText}`
    )
    this.stepCountEl_ = this.element_.querySelector(`.${CLASSES.stepCount}`)
    this.inputHolder_ = this.element_.querySelector(`.${CLASSES.inputHolder}`)
    this.submitButton_ = this.element_.querySelector(`.${CLASSES.submitButton}`)
    this.loader_ = document.querySelector(`.${CLASSES.loader}`)
    this.tweenMax_ = TweenMax
    this.stepCount_ = 2
    this.activeStep_ = 0
    this.formData_ = {}
    this.init()
  }

  /**
   * Set input error.
   * @param {String} value The error message.
   */
  setInputError(value) {
    this.errorField_.innerHTML = value
  }

  /**
   * Encode the attachment.
   * @param {InputFile} file Input file.
   * @returns {Object}
   */
  async encodeAttachment(file) {
    const toBase64 = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    try {
      return {
        filename: file.name,
        type: file.type,
        content: await toBase64(file)
      }
    } catch (error) {
      return {}
    }
  }

  /**
   * Send e-mail to server.
   * @param {Object} formValues Form values.
   */
  sendEmail(formValues) {
    const payload = { to: 'contato@digitalrepublic.com.br', ...formValues }
    const xhr = new XMLHttpRequest()
    const url =
      'https://vx3nuvayzb.execute-api.us-east-1.amazonaws.com/prod/contact'
    this.loader_.style.display = 'flex'
    xhr.open('POST', url, true)
    xhr.onreadystatechange = () => {
      const { status, readyState } = xhr
      const { successStatus, xhrSuccessState } = NUMBERS

      if (readyState === xhrSuccessState) {
        if (status === successStatus) {
          this.formContent_.style.display = 'none'
          this.loader_.style.display = 'none'
          this.successMessage_.style.display = 'block'
        } else {
          this.loader_.style.display = 'none'
          alert(`Erro: ${xhr.response}`)
        }
      }
    }
    xhr.send(JSON.stringify(payload))
  }

  /**
   * Finish form.
   */
  async finishForm() {
    this.formData_.subject = `Contato de candidato: ${document.title}`
    this.formData_.message = `Contato vindo da página ${window.location.href}`
    this.formData_.curriculum_vitae = await this.encodeAttachment(
      this.formData_.fileInput
    )
    delete this.formData_.fileInput
    this.sendEmail(this.formData_)
  }

  /**
   * Set the step count according to the step .
   */
  setStepCountHtml() {
    const { attachment } = NUMBERS
    this.stepCountEl_.innerHTML = `${this.activeStep_ + 1}/${this.stepCount_ +
      1}`
    this.submitButton_.innerHTML =
      this.activeStep_ === attachment ? 'Enviar' : 'Próximo'
  }

  /**
   * Animate input.
   * @param {HTMLElement} element The element to be animated.
   * @param {String} left Left value to animate the input.
   */
  animateInput(element, left) {
    const { transitionTime } = NUMBERS
    this.tweenMax_.to(element, transitionTime, {
      left,
      ease: Quint.easeOut
    })
  }

  /**
   * Set input holder height according to the next input.
   * @param {HTMLElement} input The input that it's going to get height from.
   */
  setInputHolderHeight(input) {
    const { inputBorder } = NUMBERS
    if (input) {
      TweenMax.set(this.inputHolder_, {
        height: input.getBoundingClientRect().height + inputBorder
      })
    }
  }

  /**
   * Go to previous step.
   */
  previousStep() {
    if (this.activeStep_ < NUMBERS.attachment) {
      this.backButton_.classList.remove(CLASSES.backButtonVisible)
      this.backButton_.setAttribute('tabindex', '-1')
    }

    const input = this.element_.querySelector(
      `[data-step="${this.activeStep_}"]`
    )
    const prevInput = this.element_.querySelector(
      `[data-step="${this.activeStep_ - 1}"]`
    )
    this.animateInput(input, '110%')
    this.animateInput(prevInput, '0')
    this.tweenMax_.delayedCall(NUMBERS.transitionTime, () => {
      input.setAttribute('tabindex', '-1')
      if (prevInput) {
        prevInput.removeAttribute('tabindex')
        prevInput.focus()
      }
    })
    this.setInputHolderHeight(prevInput)
    this.activeStep_--
    this.setStepCountHtml()
    this.setInputError('')
  }

  /**
   * Advance step.
   * @param {String} fieldName Name of the field
   * @param {String} fieldValue Value of the field
   */
  advanceStep(fieldName, fieldValue) {
    this.formData_[fieldName] = fieldValue
    if (this.activeStep_ + 1 === NUMBERS.success) {
      this.finishForm()
    } else {
      this.backButton_.classList.add(CLASSES.backButtonVisible)
      this.backButton_.removeAttribute('tabindex')
      const input = this.element_.querySelector(
        `[data-step="${this.activeStep_}"]`
      )
      const nextInput = this.element_.querySelector(
        `[data-step="${this.activeStep_ + 1}"]`
      )
      this.tweenMax_.delayedCall(NUMBERS.transitionTime, () => {
        input.setAttribute('tabindex', '-1')
        if (nextInput) {
          nextInput.removeAttribute('tabindex')
          nextInput.focus()
        }
      })
      this.animateInput(input, '-110%')
      this.animateInput(nextInput, '0')
      this.setInputHolderHeight(nextInput)
      this.activeStep_++
      this.setStepCountHtml()
      this.setInputError('')
    }
  }

  /**
   * Form submit.
   */
  submitForm() {
    const { name, email, attachment } = NUMBERS
    switch (this.activeStep_) {
      case name: {
        const invalid = validateName(this.nameInput_.value)
        if (invalid) this.setInputError(invalid)
        else this.advanceStep('name', this.nameInput_.value)
        break
      }
      case email: {
        const invalid = validateEmail(this.emailInput_.value)
        if (invalid) this.setInputError(invalid)
        else this.advanceStep('email', this.emailInput_.value)
        break
      }
      case attachment: {
        if (this.fileInput_.files.length < 1)
          this.setInputError('Campo obrigatório')
        else this.advanceStep('fileInput', this.fileInput_.files[0])
        break
      }
      default:
      // Step doesn't exists
    }
  }

  /**
   * Instance form.
   */
  instanceForm() {
    this.backButton_.setAttribute('tabindex', '-1')
    this.emailInput_.setAttribute('tabindex', '-1')
    this.fileInput_.setAttribute('tabindex', '-1')
    this.form_.addEventListener('submit', e => {
      e.preventDefault()
      this.submitForm()
    })
    this.fileInput_.addEventListener('change', e => {
      this.fileInputText_.innerHTML = e.target.files[0].name
    })
    this.backButton_.addEventListener('click', () => this.previousStep())
  }

  /**
   * Construct module.
   */
  init() {
    this.instanceForm()
  }
}

// Exports
export default SendYourResume
